import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import { common } from '@qwyk/models';

import {
    BackendQuery,
    SearchQuery,
    Shipment,
    ShipmentState,
} from './shipments.models';
import { ShipmentsActions } from './shipments.actions';

export const SHIPMENTS_FEATURE_KEY = 'shipmentsNew';

export interface State extends EntityState<Shipment> {
    selectedId?: string | number; // which Shipments record has been selected
    loading: boolean; // has the Shipments list been loaded
    error?: Error | null; // last known error (if any)
    customFields: common.CustomFieldDefinition[];
    query: SearchQuery | null;
    backendQuery: BackendQuery | null;
    backendPagination?: common.PaginationMeta;
    filterSuggestions: { [column: string]: string[] }; // popular values for filter dropdowns
    suggestionsLoading: boolean; // whether suggestions are being calculated
    sysStates: ShipmentState[] | null;
    loadingStates: boolean;
}

export interface ShipmentsPartialState {
    readonly [SHIPMENTS_FEATURE_KEY]: State;
}

export const shipmentsAdapter: EntityAdapter<Shipment> = createEntityAdapter<Shipment>();

export const initialState: State = shipmentsAdapter.getInitialState({
    loading: false,
    customFields: [],
    sysStates: null,
    query: null,
    backendQuery: null,
    loadingStates: false,
    backendPagination: null,
    filterSuggestions: {},
    suggestionsLoading: false,
});

const shipmentsReducer = createReducer(
    initialState,
    on(ShipmentsActions.loadShipments, (state, { query }) =>
        shipmentsAdapter.removeAll({
            ...state,
            loading: true,
            error: null,
            backendQuery: {
                ...state.backendQuery,
                ...(typeof query === 'object' && query ? query : {}),
            },
            backendPagination: null
        })
    ),
    on(ShipmentsActions.forceRefreshShipments, (state) =>
        shipmentsAdapter.removeAll({
            ...state,
            loading: true,
            error: null,
            backendPagination: null
        })
    ),
    on(ShipmentsActions.loadShipmentsSuccess,
        (state, { shipments, customFields, customViews, backendPagination }) => {
            const uniqueShipments = shipments.filter(
                (shipment, index, self) =>
                    index === self.findIndex(s => s.id === shipment.id)
            );
            return shipmentsAdapter.setAll(uniqueShipments, {
                ...state,
                loading: false,
                customFields,
                customViews,
                backendPagination,
            });
        }
    ),
    on(ShipmentsActions.loadShipmentsFailure, (state, { error }) =>
        shipmentsAdapter.removeAll({
            ...state,
            loading: false,
            error,
            backendPagination: null
        })
    ),
    on(ShipmentsActions.loadDraftShipments, (state, { query }) => {
        return shipmentsAdapter.removeAll({
            ...state,
            loading: true,
            error: null,
            backendQuery: {
                ...state.backendQuery,
                ...(typeof query === 'object' && query ? query : {}),
            },
        });
    }),
    on(
        ShipmentsActions.loadDraftShipmentsSuccess,
        (state, { shipments, customFields }) => {
            const uniqueShipments = shipments.filter(
                (shipment, index, self) =>
                    index === self.findIndex(s => s.id === shipment.id)
            );
            return shipmentsAdapter.setAll(uniqueShipments, {
                ...state,
                loading: false,
                customFields,
            });
        }
    ),
    on(ShipmentsActions.loadDraftShipmentsFailure, (state, { error }) =>
        shipmentsAdapter.removeAll({
            ...state,
            loading: false,
            error,
        })
    ),
    on(ShipmentsActions.searchShipments, (state, { query }) => ({
        ...state,
        query,
    })),
    on(ShipmentsActions.loadShipment, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(
        ShipmentsActions.loadShipmentSuccess,
        ShipmentsActions.assignShipmentOperatorSuccess,
        ShipmentsActions.completeShipmentSuccess,
        ShipmentsActions.cancelShipmentSuccess,
        (state, { shipment }) =>
            shipmentsAdapter.setOne(shipment, {
                ...state,
                loading: false,
            })
    ),
    on(ShipmentsActions.loadShipmentFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(ShipmentsActions.loadSysStates, state => ({
        ...state,
        loadingStates: true,
    })),
    on(ShipmentsActions.loadSysStatesSuccess, (state, { sys_states }) => {
        return {
            ...state,
            sysStates: sys_states,
            loadingStates: false,
        };
    }),
    on(ShipmentsActions.loadSysStatesFailure, state => ({
        ...state,
        loadingStates: false,
    })),
    on(
        ShipmentsActions.updateShipmentReferences,
        ShipmentsActions.generateShipmentPdf,
        ShipmentsActions.sendTrackingRequest,
        ShipmentsActions.cancelTrackingRequest,
        ShipmentsActions.updateShipmentDataTransport,
        ShipmentsActions.updateShipmentDataReferences,
        ShipmentsActions.assignShipmentOperator,
        ShipmentsActions.completeShipment,
        ShipmentsActions.cancelShipment,
        ShipmentsActions.updateShipmentName,
        state => ({
            ...state,
            loading: true,
            error: null,
        })
    ),
    on(
        ShipmentsActions.updateShipmentPartiallySuccess,
        (state, { shipmentId, update }) =>
            shipmentsAdapter.updateOne(
                {
                    id: shipmentId,
                    changes: update,
                },
                {
                    ...state,
                    loading: false,
                }
            )
    ),
    on(ShipmentsActions.generateShipmentPdfSuccess, state => ({
        ...state,
        loading: false,
    })),
    on(ShipmentsActions.cancelTrackingRequestSuccess, (state, { shipmentId }) =>
        shipmentsAdapter.updateOne(
            {
                id: shipmentId,
                changes: { tracking_request_status: null },
            },
            {
                ...state,
                loading: false,
            }
        )
    ),
    on(
        ShipmentsActions.sendTrackingRequestSuccess,
        (state, { shipmentId, response }) =>
            shipmentsAdapter.updateOne(
                {
                    id: shipmentId,
                    changes: response,
                },
                {
                    ...state,
                    loading: false,
                }
            )
    ),
    on(
        ShipmentsActions.updateShipmentDataTransportSuccess,
        ShipmentsActions.updateShipmentDataReferencesSuccess,
        (state, { shipmentId, data }) =>
            shipmentsAdapter.updateOne(
                {
                    id: shipmentId,
                    changes: { data },
                },
                {
                    ...state,
                    loading: false,
                }
            )
    ),
    on(
        ShipmentsActions.updateShipmentPartiallyFailure,
        ShipmentsActions.generateShipmentPdfFailure,
        ShipmentsActions.cancelTrackingRequestFailure,
        ShipmentsActions.sendTrackingRequestFailure,
        ShipmentsActions.updateShipmentDataTransportFailure,
        ShipmentsActions.updateShipmentDataReferencesFailure,
        ShipmentsActions.assignShipmentOperatorFailure,
        ShipmentsActions.completeShipmentFailure,
        ShipmentsActions.cancelShipmentFailure,
        (state, { error }) => ({
            ...state,
            loading: false,
            error,
        })
    ),
    on(ShipmentsActions.selectShipment, (state, { selectedId }) => ({
        ...state,
        selectedId,
    })),
    on(ShipmentsActions.unselectShipment, state => ({
        ...state,
        selectedId: null,
    })),
    on(ShipmentsActions.tagShipment, (state, { entityId, tag }) => {
        const entity = state.entities[entityId];
        if (!entity) {
            return state;
        }
        const existingTags = entity.tags || [];
        return shipmentsAdapter.updateOne(
            {
                id: entityId,
                changes: {
                    tags: [...existingTags, tag],
                },
            },
            state
        );
    }),
    on(ShipmentsActions.updateTagInShipment, (state, { tag, tagId, entityId }) => {
        const resolvedTagId = tagId ?? tag?.id;
        if (!resolvedTagId) {
            return state;
        }
        const replaceTag = (tags: common.CustomTag[]) =>
            tags.map(t => t.id === resolvedTagId ? { ...t, ...tag } : t);

        if (entityId) {
            const entity = state.entities[entityId];
            if (!entity?.tags) {
                return state;
            }
            return shipmentsAdapter.updateOne(
                { id: entityId, changes: { tags: replaceTag(entity.tags) } },
                state
            );
        }

        const updates = Object.values(state.entities)
            .filter(entity => entity?.tags?.some(t => t.id === resolvedTagId))
            .map(entity => ({
                id: entity.id,
                changes: { tags: replaceTag(entity.tags) },
            }));
        return shipmentsAdapter.updateMany(updates, state);
    }),
    on(ShipmentsActions.removeTagFromShipment, (state, { entityId, tagId }) => {
        const tags = state.entities[entityId].tags
            .filter(e => e !== undefined)
            .filter(t => t.id !== tagId);

        return shipmentsAdapter.updateOne(
            {
                id: entityId,
                changes: {
                    tags,
                },
            },
            state
        );
    }),
    on(ShipmentsActions.resetLoadShipmentsQuery, state => ({
        ...state,
        backendQuery: null,
    })),
    on(ShipmentsActions.resetAction, state => ({
        ...state,
        ...initialState,
    })),
    on(ShipmentsActions.dismissError, state => ({
        ...state,
        error: null,
    })),
    on(ShipmentsActions.calculateFilterSuggestions, state => ({
        ...state,
        suggestionsLoading: true,
    })),
    on(ShipmentsActions.calculateFilterSuggestionsSuccess, (state, { suggestions }) => ({
        ...state,
        filterSuggestions: suggestions,
        suggestionsLoading: false,
    })),
    on(ShipmentsActions.calculateFilterSuggestionsFailure, (state, { error }) => ({
        ...state,
        error,
        suggestionsLoading: false,
    }))
);

export function reducer(state: State | undefined, action: Action) {
    return shipmentsReducer(state, action);
}
